import useEditorProps from '@hooks/useEditorProps';
import useEmailEditor from '@hooks/useEmailEditor';
import { KeyboardArrowDownRounded } from '@mui/icons-material';
import { Button, ButtonGroup, Menu, MenuItem } from '@mui/material';
import html2canvas from 'html2canvas';
import { useSnackbar } from 'notistack';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ReactEditor, useSlate } from 'slate-react';

function PrimaryButton({ showTemplates }) {
    const { t, i18n } = useTranslation();
    const { saveTemplate, title, navigateBack } = useEditorProps();
    const { saveEmail } = useEmailEditor();
    const { enqueueSnackbar } = useSnackbar();
    const editor = useSlate();
    const [anchorEL, setAnchorEL] = useState(null);

    const handleSaveTemplate = () => {
        const nodeElement = ReactEditor.toDOMNode(editor, editor?.children[0]);

        html2canvas(nodeElement)
            .then(canvas => {
                if (!canvas) throw new Error('Failed to create the canvas');

                const tempCanvas = document.createElement('canvas');
                const tempCtx = tempCanvas.getContext('2d');

                if (!tempCtx)
                    throw new Error(
                        'Failed to get the context of the temporary canvas',
                    );

                const aspectRatio = canvas.width / canvas.height;
                const newHeight = 400;
                const newWidth = newHeight * aspectRatio;

                tempCanvas.width = newWidth;
                tempCanvas.height = newHeight;

                tempCtx.drawImage(canvas, 0, 0, newWidth, newHeight);

                tempCanvas.toBlob(blob => {
                    const imageURL = URL.createObjectURL(blob);
                    const template = {
                        name: title,
                        thumbnail: imageURL,
                        template: [editor.children[0]],
                    };
                    saveTemplate(template);
                });
            })
            .then(() => {
                enqueueSnackbar(t('Template saved'), { variant: 'success' });
                setAnchorEL(null);
            })
            .catch(error => {
                console.error('Error:', error);
            });
    };

    return (
        <>
            <ButtonGroup
                variant="contained"
                sx={{
                    boxShadow: 0,
                    '& .MuiButtonGroup-grouped:not(:last-of-type)': {
                        borderRightColor: 'white',
                    },
                }}
            >
                <Button
                    onClick={() => {
                        saveEmail().then(() => {
                            navigateBack();
                        });
                    }}
                >
                    {t('Save & Exit')}
                </Button>
                <Button
                    onClick={e => setAnchorEL(e.currentTarget)}
                    sx={{
                        padding: 0,
                    }}
                >
                    <KeyboardArrowDownRounded />
                </Button>
            </ButtonGroup>
            <Menu
                open={Boolean(anchorEL)}
                onClose={() => setAnchorEL(null)}
                anchorEl={anchorEL}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: i18n.dir() === 'rtl' ? 'left' : 'right',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: i18n.dir() === 'rtl' ? 'left' : 'right',
                }}
                slotProps={{
                    paper: {
                        sx: {
                            borderRadius: 4,
                        },
                    },
                }}
            >
                <MenuItem onClick={() => setAnchorEL(null)}>
                    {t('Save & Exit')}
                </MenuItem>
                <MenuItem onClick={handleSaveTemplate}>
                    {t('Save as Template')}
                </MenuItem>
                <MenuItem
                    onClick={() => {
                        setAnchorEL(null);
                        showTemplates();
                    }}
                >
                    {t('Change Template')}
                </MenuItem>
            </Menu>
        </>
    );
}

export default PrimaryButton;
